import { Spin } from 'antd'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Outlet, useParams } from 'react-router'
import { deleteRequest, get, post, put, putMultipart } from '../../Common/api'
import GlobalContext from '../GlobalContext'

const BoxContext = createContext()
export default BoxContext

export const BoxContextProvider = () => {
  const [loading, setLoading] = useState(false)
  const [box, setBox] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [days, setDays] = useState([])
  const [slots, setSlots] = useState([])
  const [periods, setPeriods] = useState([])
  const [owners, setOwners] = useState([])
  const [appointmentsDate, setAppointmentsDates] = useState([])
  const { id } = useParams()
  const { notificationApi } = useContext(GlobalContext)

  const setCurrentBox = (json) => {
    json.gps_point = json.gps_point.replace(/POINT *\(/, '').replace(')', '')
    setBox(json)
  }

  const loadBox = async () => {
    setLoading(true)
    await get({
      url: `admin/boxes/${id}`,
      params: {},
      onSuccess: (json) => {
        setLoading(false)
        setCurrentBox(json)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const loadAppointments = async (boxId, date) => {
    if (!boxId) return
    setAppointmentsDates([...appointmentsDate, date.toString()])
    setLoading(true)
    await get({
      url: 'admin/appointments',
      params: { box_id: boxId, date: date.toString(), scope: 'calendar' },
      onSuccess: (json) => {
        setLoading(false)
        setAppointments([...appointments, ...json])
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const loadDays = async (boxId, date) => {
    if (!boxId) return
    setLoading(true)
    await get({
      url: 'admin/days',
      params: { box_id: boxId, start_date: date.toString() },
      onSuccess: (json) => {
        setLoading(false)
        setDays(json)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const loadSlots = async (start, end) => {
    setLoading(true)
    await get({
      url: `admin/boxes/${id}/slots`,
      params: { range_start: start.toString(), range_end: end.toString() },
      onSuccess: (json) => {
        setLoading(false)
        setSlots(json)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const loadPeriods = async () => {
    setLoading(true)
    await get({
      url: `admin/boxes/${id}/periods`,
      params: { },
      onSuccess: (json) => {
        setLoading(false)
        setPeriods(json)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const loadDefaultDays = async (boxId) => {
    if (!boxId) return
    setLoading(true)
    await get({
      url: 'admin/days/default_index',
      params: { box_id: boxId },
      onSuccess: (json) => {
        setLoading(false)
        setDays(json)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const savePeriod = async (params, form, onSuccess) => {
    setLoading(true)
    params = { ...params, start: params.range[0], end: params.range[1], box_id: box.id }
    params.id ? updatePeriod(params, form, onSuccess) : createPeriod(params, form, onSuccess)
  }

  const createPeriod = async (params, form, onSuccess) => {
    await post({
      url: 'admin/periods',
      params,
      onSuccess: (json) => {
        const day = days.find((day) => day.id === json.day_id)
        day.periods = [...day.periods, json]
        setLoading(false)
        onSuccess()
      },
      onFailure: (errors) => {
        form.setFields(
          Object.keys(errors).map((key) => ({
            name: key,
            errors: errors[key]
          }))
        )
        setLoading(false)
      }
    })
  }

  const updatePeriod = async (params, form, onSuccess) => {
    await put({
      url: `admin/periods/${params.id}`,
      params,
      onSuccess: (json) => {
        const day = days.find((day) => day.id === json.day_id)
        day.periods = [...day.periods, json]
        setLoading(false)
        onSuccess()
      },
      onFailure: (errors) => {
        form.setFields(
          Object.keys(errors).map((key) => ({
            name: key,
            errors: errors[key]
          }))
        )
        setLoading(false)
      }
    })
  }

  const deletePeriod = async (id, onSuccess) => {
    setLoading(true)
    await deleteRequest({
      url: `admin/periods/${id}`,
      params: {},
      notification: { api: notificationApi, state: 'delete' },
      onSuccess: (json) => {
        const day = days.find((day) => day.id === json.day_id)
        day.periods = day.periods.filter(period => period.id !== id)
        setDays([...days, day])
        setLoading(false)
        onSuccess()
      }
    })
  }

  const updateBox = async (params, form) => {
    setLoading(true)
    if (typeof params.picture === 'string') {
      delete params.picture
    }
    if (typeof params.waiting_time === 'string') {
      delete params.waiting_time
    }
    params.intervener_ids = form.getFieldValue('interveners').map(i => i.id)
    await putMultipart({
      url: `admin/boxes/${box.id}`,
      params,
      notification: { api: notificationApi, state: 'update' },
      onSuccess: (json) => {
        setLoading(false)
        form.setFields(
          Object.keys(json).map((key) => ({
            name: key,
            errors: null
          }))
        )
        setCurrentBox(json)
      },
      onFailure: (errors) => {
        setLoading(false)
        form.setFields(
          Object.keys(errors).map((key) => ({
            name: key,
            errors: errors[key]
          }))
        )
      }
    })
  }

  const loadOwners = async () => {
    setLoading(true)
    await get({
      url: 'admin/owners',
      params: {},
      onSuccess: (res) => {
        setLoading(false)
        setOwners(res.owners)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const saveAppointment = async (params, form, afterSuccess) => {
    params = { ...params, begin_date: params.range[0], end_date: params.range[1], box_id: box.id }
    setLoading(true)
    await post({
      url: 'admin/appointments',
      params,
      notification: { api: notificationApi, state: 'update' },
      onSuccess: (json) => {
        setLoading(false)
        setAppointments([...appointments, json])
        const updatedSlots = slots
        json.slots.forEach(slot => {
          const index = slots.findIndex(s => s.id === slot.id)
          updatedSlots[index] = slot
        })
        setSlots([...updatedSlots])
        afterSuccess()
      },
      onFailure: (errors) => {
        setLoading(false)
        form.setFields(
          Object.keys(errors).map((key) => ({
            name: key,
            errors: errors[key]
          }))
        )
      }
    })
  }

  useEffect(() => {
    loadBox()
    loadOwners()
    loadPeriods()
  }, [])

  const values = {
    loading,
    box,
    owners,
    appointments,
    loadAppointments,
    saveAppointment,
    updateBox,
    loadDefaultDays,
    days,
    savePeriod,
    deletePeriod,
    loadDays,
    loadSlots,
    slots,
    periods
  }
  return <BoxContext.Provider value={values}>
    <Spin spinning={loading}> <Outlet/> </Spin>
  </BoxContext.Provider>
}
